import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productionAPI } from '../services/api';
import './Production.css';

interface BatchAllocation {
  id: number;
  order_number: string;
  order_id: number;
  line_number: string;
  customer_name: string;
  customer_id: number;
  allocated_weight: number;
  package_type: string;
  package_type_display: string;
  number_of_packages: number;
  delivery_date: string;
  status: string;
}

interface BatchDetail {
  id: number;
  batch_number: string;
  formula_id: number;
  formula_name: string;
  feed_form: string;
  feed_form_display: string;
  planned_weight: number;
  actual_weight: number | null;
  status: string;
  status_display: string;
  planned_date: string | null;
  priority: number;
  started_at: string | null;
  completed_at: string | null;
  completed_by: string | null;
  operator: string | null;
  notes: string;
  allocations: BatchAllocation[];
  orders_count: number;
  total_allocated: number;
  ship_dates: string[];
  created_at: string;
  updated_at: string;
}

const ProductionBatchDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [batch, setBatch] = useState<BatchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadBatch();
  }, [id]);

  const loadBatch = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError('');
      const response = await productionAPI.getBatch(parseInt(id));
      setBatch(response.data);
    } catch (err: any) {
      console.error('Error loading batch:', err);
      setError('Failed to load batch details');
    } finally {
      setLoading(false);
    }
  };

  const handleStartBatch = async () => {
    if (!batch) return;

    try {
      await productionAPI.startBatch(batch.id);
      loadBatch();
    } catch (err: any) {
      console.error('Error starting batch:', err);
      alert('Failed to start batch: ' + (err.response?.data?.error || err.message));
    }
  };

  // Batch completion is NOT done from this page anymore. The old inline
  // form called the retired /production/batches/<id>/complete/ endpoint
  // (410 Gone — it skipped raw-material decrement and disposition).
  // Completion lives in the Mixing Workspace, whose disposition flow
  // (complete_mixing) decrements ingredients and dispositions the whole
  // batch atomically — we navigate there instead.
  const handleGoToMixing = () => {
    if (!batch) return;
    navigate(`/production/mixing/${batch.id}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'PLANNED':
        return 'status-planned';
      case 'IN_PRODUCTION':
      case 'IN_PROGRESS':
        return 'status-in-production';
      case 'COMPLETED':
        return 'status-completed';
      case 'PACKAGING':
      case 'PACKAGED':
        return 'status-packaged';
      case 'SHIPPED':
        return 'status-shipped';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="production-page">
        <div className="production-loading">
          <div className="loading-spinner"></div>
          <p>Loading batch details...</p>
        </div>
      </div>
    );
  }

  if (error || !batch) {
    return (
      <div className="production-page">
        <div className="batch-detail-error">
          <p>{error || 'Batch not found'}</p>
          <button className="btn btn-primary" onClick={() => navigate('/production')}>
            Back to Production
          </button>
        </div>
      </div>
    );
  }

  const canStart = batch.status === 'PLANNED';
  const canComplete = batch.status === 'IN_PRODUCTION' || batch.status === 'IN_PROGRESS';
  const isCompleted = batch.status === 'COMPLETED' || batch.status === 'PACKAGED' || batch.status === 'SHIPPED';

  return (
    <div className="production-page">
      {/* Header */}
      <div className="batch-page-header">
        <button className="btn btn-secondary back-btn" onClick={() => navigate('/production')}>
          &larr; Back to Production
        </button>
        <div className="batch-title">
          <h1>{batch.batch_number}</h1>
          <span className={`batch-status-large ${getStatusClass(batch.status)}`}>
            {batch.status_display}
          </span>
        </div>
      </div>

      <div className="batch-detail-layout">
        {/* Left Column - Batch Info */}
        <div className="batch-info-column">
          <div className="detail-card">
            <div className="detail-card-header">
              <h2>{batch.formula_name}</h2>
              <span className={`batch-form form-${batch.feed_form.toLowerCase()}`}>
                {batch.feed_form_display || batch.feed_form}
              </span>
            </div>

            <div className="detail-info-grid">
              <div className="info-item">
                <span className="info-label">Planned Weight</span>
                <span className="info-value large">{batch.planned_weight.toLocaleString()} lbs</span>
              </div>
              {batch.actual_weight && (
                <div className="info-item">
                  <span className="info-label">Actual Weight</span>
                  <span className="info-value large">{batch.actual_weight.toLocaleString()} lbs</span>
                </div>
              )}
              <div className="info-item">
                <span className="info-label">Planned Date</span>
                <span className="info-value">
                  {batch.planned_date ? formatDate(batch.planned_date) : 'Not set'}
                </span>
              </div>
              {batch.started_at && (
                <div className="info-item">
                  <span className="info-label">Started</span>
                  <span className="info-value">{formatDateTime(batch.started_at)}</span>
                </div>
              )}
              {batch.completed_at && (
                <div className="info-item">
                  <span className="info-label">Completed</span>
                  <span className="info-value">{formatDateTime(batch.completed_at)}</span>
                </div>
              )}
              {batch.completed_by && (
                <div className="info-item">
                  <span className="info-label">Completed By</span>
                  <span className="info-value">{batch.completed_by}</span>
                </div>
              )}
            </div>
          </div>

          {/* Orders in Batch */}
          <div className="detail-card">
            <h3>Orders in This Batch ({batch.orders_count})</h3>
            <div className="orders-list">
              <table className="allocations-table">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Weight</th>
                    <th>Package</th>
                    <th>Ship Date</th>
                  </tr>
                </thead>
                <tbody>
                  {batch.allocations.map((alloc) => (
                    <tr key={alloc.id}>
                      <td>
                        <a
                          href={`/orders/${alloc.order_id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/orders/${alloc.order_id}`);
                          }}
                        >
                          #{alloc.order_number}
                        </a>
                      </td>
                      <td>{alloc.customer_name}</td>
                      <td>{alloc.allocated_weight.toLocaleString()} lbs</td>
                      <td>
                        {alloc.package_type === 'BULK'
                          ? 'Bulk'
                          : `${alloc.number_of_packages} ${alloc.package_type_display || alloc.package_type}`}
                      </td>
                      <td>{formatDate(alloc.delivery_date)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}>
                      <strong>Total Allocated:</strong>
                    </td>
                    <td colSpan={3}>
                      <strong>{batch.total_allocated.toLocaleString()} lbs</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Notes */}
          {batch.notes && (
            <div className="detail-card">
              <h3>Notes</h3>
              <p className="batch-notes-text">{batch.notes}</p>
            </div>
          )}
        </div>

        {/* Right Column - Actions */}
        <div className="batch-actions-column">
          {/* Status Progress */}
          <div className="detail-card">
            <h3>Status</h3>
            <div className="status-progress">
              <div className={`progress-step ${batch.status !== 'PLANNED' ? 'completed' : 'active'}`}>
                <div className="step-dot"></div>
                <span>Planned</span>
              </div>
              <div className={`progress-step ${['IN_PRODUCTION', 'IN_PROGRESS'].includes(batch.status) ? 'active' : isCompleted ? 'completed' : ''}`}>
                <div className="step-dot"></div>
                <span>In Production</span>
              </div>
              <div className={`progress-step ${batch.status === 'COMPLETED' ? 'active' : ['PACKAGING', 'PACKAGED', 'SHIPPED'].includes(batch.status) ? 'completed' : ''}`}>
                <div className="step-dot"></div>
                <span>Milling Complete</span>
              </div>
              <div className={`progress-step ${['PACKAGING', 'PACKAGED'].includes(batch.status) ? 'active' : batch.status === 'SHIPPED' ? 'completed' : ''}`}>
                <div className="step-dot"></div>
                <span>Packaging</span>
              </div>
              <div className={`progress-step ${batch.status === 'SHIPPED' ? 'completed' : ''}`}>
                <div className="step-dot"></div>
                <span>Shipped</span>
              </div>
            </div>
          </div>

          {/* Start Production */}
          {canStart && (
            <div className="detail-card action-card">
              <h3>Start Production</h3>
              <p>Ready to begin milling this batch?</p>
              <button className="btn btn-primary btn-large" onClick={handleStartBatch}>
                Start Production
              </button>
            </div>
          )}

          {/* Complete Batch — happens in the Mixing Workspace (ingredient
              check + disposition), not on this page. The old inline form
              called a retired endpoint and returned an error. */}
          {canComplete && (
            <div className="detail-card action-card">
              <h3>Complete Batch</h3>
              <p>
                Batch completion runs through the Mixing Workspace, where
                ingredients are verified and the finished feed is
                dispositioned (orders, make-up, feed bank / retail excess).
              </p>
              <button className="btn btn-success btn-large" onClick={handleGoToMixing}>
                Complete in Mixing Workspace →
              </button>
            </div>
          )}

          {/* Completed Info */}
          {isCompleted && (
            <div className="detail-card completed-card">
              <h3>Batch Completed</h3>
              <div className="completed-info">
                <p>
                  <strong>Actual Weight:</strong> {batch.actual_weight?.toLocaleString()} lbs
                </p>
                {batch.completed_at && (
                  <p>
                    <strong>Completed:</strong> {formatDateTime(batch.completed_at)}
                  </p>
                )}
                {batch.completed_by && (
                  <p>
                    <strong>By:</strong> {batch.completed_by}
                  </p>
                )}
              </div>

              {batch.status === 'COMPLETED' && (
                <button
                  className="btn btn-primary btn-large"
                  onClick={() => navigate(`/production/packaging/${batch.id}`)}
                >
                  Go to Packaging
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductionBatchDetail;
